import { LazyQueryHookOptions, QueryResult } from "@apollo/client";
import { CurrentUserWithAccountsDocument } from "@/apollo/types";
import { useLatestValueRef } from "@/hooks/useLatestValueRef";
import * as Sentry from "@sentry/react";
import { useUnmountEffect } from "@/hooks/useUnmountEffect";
import { useCallback, useRef, useState } from "react";

type CallbackOptions = {
  onStatusCheckCompleted: () => void;
  onStatusCheckError: (error: Error) => void;
};

type UsePollDWConnectionStatusOptions<TData> = {
  getStatus: (
    options?: Partial<LazyQueryHookOptions<TData, any>>,
  ) => Promise<QueryResult<TData, any>>;
  selectStatus: (data: TData) => {
    status: string;
    error?: string | null;
  } | null;
};

/** The job status can expire out of redis, and a dying worker never writes one at all. */
const STATUS_POLL_TIMEOUT_MS = 10 * 60 * 1000;

const TIMEOUT_MESSAGE = "The data warehouse setup timed out";
const LOST_JOB_MESSAGE = "We lost track of the data warehouse setup";

/**
 * Poll the connection status of a managed data warehouse
 */
export function usePollDWConnectionStatus<TData>(
  options: UsePollDWConnectionStatusOptions<TData>,
) {
  const optionsRef = useLatestValueRef(options);

  const [isConnectionEstablished, setIsConnectionEstablished] = useState(false);

  const deadlineRef = useRef<ReturnType<typeof setTimeout>>();
  useUnmountEffect(() => clearTimeout(deadlineRef.current));

  const startPeriodicStatusCheck = useCallback(
    async ({
      onStatusCheckCompleted,
      onStatusCheckError,
      ...options
    }: Parameters<UsePollDWConnectionStatusOptions<TData>["getStatus"]>[0] &
      CallbackOptions) => {
      clearTimeout(deadlineRef.current);

      const poll = { settled: false, stop: () => {} };
      const settle = async (finish: () => void | Promise<void>) => {
        if (poll.settled) {
          return;
        }
        poll.settled = true;
        clearTimeout(deadlineRef.current);
        try {
          poll.stop();
        } catch {}
        await finish();
      };

      const fail = (message: string) =>
        settle(() => {
          setIsConnectionEstablished(false);
          onStatusCheckError(new Error(message));
        });

      deadlineRef.current = setTimeout(() => {
        void fail(TIMEOUT_MESSAGE);
      }, STATUS_POLL_TIMEOUT_MS);

      let hasSeenJob = false;

      const { client, stopPolling } = await optionsRef.current.getStatus({
        ...options,
        pollInterval: 2000,
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true,
        onCompleted: async (res) => {
          const result = optionsRef.current.selectStatus(res);
          if (result == null) {
            return;
          }
          if (result.status === "empty") {
            // Normal until the backend writes the start status; after that, it expired.
            if (hasSeenJob) {
              await fail(LOST_JOB_MESSAGE);
            }
            return;
          }
          hasSeenJob = true;
          if (result.status === "done") {
            await settle(async () => {
              try {
                await client.refetchQueries({
                  include: [CurrentUserWithAccountsDocument],
                });
              } catch (error) {
                // The warehouse is connected; rejecting here would strand a cancelled deadline.
                Sentry.captureException(error);
              }
              setIsConnectionEstablished(true);
              onStatusCheckCompleted();
            });
          }
          if (result.status === "error" || result.status === "cancelled") {
            await fail(
              result.error ?? "The connection to the data warehouse failed",
            );
          }
        },
      });

      poll.stop = stopPolling;
      // A deadline that fired while the query was still starting up had nothing to stop yet.
      if (poll.settled) {
        try {
          stopPolling();
        } catch {}
      }
    },
    [optionsRef],
  );

  return {
    startPeriodicStatusCheck,
    isConnectionEstablished,
  };
}
